import BaseModel from "lib/model";
import Refunds from "collections/refunds";
import uuid from "uuid/v4";
import moment from "moment";
import Globalize from "globalize";

export default BaseModel.extend({
    idAttribute: "_id",

    dataTypes: {
        uuid: {
            set(newVal) {
                return {
                    val: newVal,
                    type: "uuid",
                };
            },
            default() {
                return uuid();
            },
        },
    },

    props: {
        _id: {
            type: "string",
        },
        uniqueId: {
            type: "uuid",
            required: true,
        },
        type: {
            type: "string",
            required: true,
            default: "payment",
        },
        created_on: {
            type: "date",
            require: true,
        },
        processed_on: {
            type: "date",
            require: false,
        },
        description: {
            type: "string",
        },
        brand_payment_account_id: {
            type: "string",
        },
        card_id: {
            type: "string",
        },
        status: {
            type: "string",
            required: true,
            default: "pending",
        },
        amount: {
            type: "number",
            required: true,
        },
        support_capture_for_less: {
            type: "boolean",
            default: false,
        },
        currency_code: {
            type: "string",
            required: true,
        },
        original_transaction_id: {
            type: "string",
        },
        original_transaction_token: {
            type: "string",
        },
        payment_method: {
            type: "string",
        },
        bank_name: {
            type: "string",
        },
        card_type: {
            type: "string",
        },
        last_four: {
            type: "string",
        },
        refund_amount: {
            type: "number",
        },
        paymentGroupId: {
            type: "string",
        },
        display_name: {
            type: "string",
        },
        detail: {
            type: "object",
            default: function () {
                return {};
            },
        },
        area: {
            type: "string",
        },
        source: {
            type: "string",
        },
        capture: {
            type: "object",
        },
        browser_info: {
            type: "string",
        },
        cc: {
            type: "string",
        },
        card_owner: {
            type: "string",
        },
        created_by: {
            type: "string",
        },
        created_by_name: {
            type: "string",
        },
        created_by_email: {
            type: "string",
        },
        invoiceId: {
            type: "string",
        },
    },

    collections: {
        refunds: Refunds,
    },

    session: {
        invalidRefundAmount: {
            type: "boolean",
            default: false,
        },
    },

    derived: {
        max_refund_amount: {
            cache: false,
            fn: function () {
                let amountRemaining = this.amount;
                this.refunds.forEach(function (refund) {
                    if (refund.status !== "declined") {
                        amountRemaining = amountRemaining - refund.amount;
                    }
                });
                return Math.round(amountRemaining * 100) / 100;
            },
        },

        displayStatus: {
            deps: ["status"],
            fn: function () {
                let amountDescription = Globalize.formatMessage("Attempted Amount");
                if (["resolved", "captured", "authorized"].indexOf(this.status) !== -1) {
                    if (this.type === "payment" || this.type === "capture") {
                        amountDescription = Globalize.formatMessage("Billed Amount");
                    } else if (this.type === "authorize") {
                        amountDescription = Globalize.formatMessage("Authorized Amount");
                    }
                }

                return amountDescription;
            },
        },

        formatted_created_on_day: {
            deps: ["created_on"],
            fn: function () {
                return moment(this.created_on).format("MMM DD, YYYY");
            },
        },
        formatted_created_on_time: {
            deps: ["created_on"],
            fn: function () {
                return moment(this.created_on).local().format("h:mm a");
            },
        },
        error_description: {
            deps: ["detail"],
            fn: function () {
                let errorDescription = "";

                if (this.status === "declined") {
                    const errorResponse = JSON.parse(this.detail.response);
                    errorDescription = this.generateDisplayErrorMessage(errorResponse);
                }

                return errorDescription;
            },
        },
    },

    generateDisplayErrorMessage: function (errorObj) {
        let message = "";
        const genericAPIErrorCodes = [
            "1001",
            "1002",
            "1003",
            "1004",
            "1005",
            "1006",
            "1007",
            "1008",
            "1009",
            "1010",
            "1011",
            "1012",
            "1013",
            "1014",
            "1015",
        ];
        const invalidAmountError = ["1003"];
        const paymentErrorCodes = ["2001", "2002", "2005", "2006", "2007", "2008"];
        const accountErrorCodes = ["3001", "3002", "3003", "3004", "3005", "3006", "3007"];

        let rejectCode = "";

        if (errorObj && errorObj.error_code) {
            rejectCode = errorObj.error_code.toString();
        } else if (errorObj && errorObj.response && errorObj.response.errorCode) {
            rejectCode = errorObj.response.errorCode.toString();
        }

        if (genericAPIErrorCodes.indexOf(rejectCode) !== -1) {
            message = Globalize.formatMessage("Oops! We had a problem processing your payment");
        } else if (invalidAmountError.indexOf(rejectCode) !== -1) {
            if (errorObj.error_description === "App fee can not be greater than 20% of the amount") {
                //WePay's fee + % rate on a given transaction can't be more than 20% of the transaction amount.
                message = Globalize.formatMessage("Minimum transaction amount is |amount|.", { amount: "$2.00" });
            } else if (errorObj.reject_code === "1003: access_denied") {
                message = `${Globalize.formatMessage("Oops! We had a problem processing your payment")}.`;
            } else {
                message = errorObj.error_description;
            }
        } else if (paymentErrorCodes.indexOf(rejectCode) !== -1) {
            message = errorObj.error_description;
        } else if (accountErrorCodes.indexOf(rejectCode) !== -1) {
            //view should filter this out so buyers never would see this
            message = Globalize.formatMessage(
                "There was a problem with your merchant services account. Please login to your WePay account or email support@wepay.com to resolve the issue."
            );
        } else {
            message = Globalize.formatMessage("Oops! The bank declined this transaction for an unspecified reason");
        }

        return message;
    },

    void: function (invoiceId, successCallback, failureCallback) {
        this.sync("patch", this, {
            //this invoiceId === paymentGroupId
            url: `/payment-group/${invoiceId}/transaction/${this._id}/void`,
            success: (data) => {
                successCallback(data);
            },
            error: (err) => {
                failureCallback(err);
            },
        });
    },
});
